/**
 * The public state-map API.
 *
 * Same-origin requests on purpose: the tenant is resolved from the subdomain by
 * callbox at the proxy, which injects X-TENANT before Django ever sees the
 * request. The browser neither knows nor sends the tenant — pointing these at
 * another origin would silently drop the header and return a 404.
 */

export type StateProperties = {
  full_name: string;
  abbreviated_name: string;
  opted_in: boolean;
  sgo_registered: boolean;
  /** Whether this state currently has a page at /pages/state/<abbr>/, per the
   * tenant's Organization.state_page_visibility setting — drives map
   * clickability. Independent of sgo_registered/opted_in: a tenant can widen
   * or narrow which states get a page without that meaning the same thing as
   * "registered" or "operating" here. */
  has_page: boolean;
  /** [longitude, latitude], guaranteed to fall inside the state's own outline. */
  label_point: [number, number];
};

export type StateFeature = GeoJSON.Feature<GeoJSON.MultiPolygon | GeoJSON.Polygon, StateProperties>;

export type StateMapData = GeoJSON.FeatureCollection<
  GeoJSON.MultiPolygon | GeoJSON.Polygon,
  StateProperties
> & {
  /** The frame to fit. Absent only from a payload with nothing to draw. */
  bbox?: [number, number, number, number];
};

export class NotFound extends Error {}

async function getMap(url: string, signal?: AbortSignal): Promise<StateMapData> {
  const response = await fetch(url, {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (response.status === 404) {
    throw new NotFound(url);
  }
  if (!response.ok) {
    throw new Error(`${url} responded ${response.status}`);
  }
  return response.json();
}

/** Every state touching the organization's registered footprint. */
export function fetchStateMap(signal?: AbortSignal): Promise<StateMapData> {
  return getMap('/api/states/map/', signal);
}

/**
 * One state, framed on itself — behind a state's PageTemplate page (see
 * `tenant_site.page_templates.StatePageTemplate` and the `StateTemplatePage`
 * section). 404s (surfaced as `NotFound`) when the state isn't currently
 * visible under the tenant's `state_page_visibility` mode.
 */
export function fetchStateDetailMap(abbreviation: string, signal?: AbortSignal): Promise<StateMapData> {
  return getMap(`/api/states/${abbreviation.toLowerCase()}/map/`, signal);
}

export function registeredStates(data: StateMapData): StateFeature[] {
  return data.features.filter((feature) => feature.properties.sgo_registered);
}

export type OptedInState = { abbreviated_name: string; full_name: string; link: boolean };

/**
 * Every state that has opted into the federal credit, each flagged with whether
 * it currently has a page at /pages/state/<abbr>/ for this tenant (`link`, the
 * same rule as `has_page` on the map). Deliberately not derived from
 * `fetchStateMap`: that payload covers only the states touching this tenant's
 * registered footprint, so it omits opted-in states outside it (Alaska, for a
 * contiguous footprint). Tenant-scoped like the maps — `link` is per tenant, so
 * unlike the platform roster this needs the X-TENANT callbox injects.
 */
export async function fetchOptedInStates(signal?: AbortSignal): Promise<OptedInState[]> {
  const response = await fetch('/api/states/opted-in/', {
    signal,
    headers: { Accept: 'application/json' },
  });
  if (!response.ok) throw new Error(`/api/states/opted-in/ responded ${response.status}`);
  return response.json();
}
